import React, { useContext } from 'react';

import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogTitle,
  ActionableDialogContent,
  ActionableDialogContentDivider,
} from '@components/Dialog';
import { DeliveryInstructionContext } from '@components/utilities/Bookings/helpers';
import { VerticalSpace } from '@components/VerticalSpace';
import { instructionStrings } from '@data/instruction';
import {
  DeliveryInstructionForm,
  getDeliveryErrorLabels,
  validateDeliveryInstructionForm,
} from '@models/DeliveryInstruction';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { useFormikContext } from 'formik';

const strings = createStrings({
  ja: {
    title: '送信の確認',
    infoIsNotEnough: `必須項目の入力が不足しているため、「${instructionStrings.deliveryFinalConfirmation}」を完了できませんでした。`,
    confirmErrorsPrompt: '次の箇所を再度ご確認ください：',
    thereIsEmptyField: (isAdmin: boolean) =>
      `入力欄が空欄、もしくは選択肢が選ばれていないようです。再度ご確認のうえ、${
        isAdmin ? '保存' : '送信'
      }ボタンを押してしてください。`,
  },
});

const useStyles = makeMuiStyles((_theme, common) => ({
  dialog: {
    maxWidth: '960px',
  },
  contentContainer: {
    padding: '0 1rem 2rem 1rem',
  },
  buttonContainer: {
    ...common.flexInline,
    ...common.justifyCenter,
    padding: '1.25rem 2rem',
  },
}));

interface DeliveryInstructionAlertDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeliveryInstructionAlertDialog: React.FC<DeliveryInstructionAlertDialogProps> = (
  props,
) => {
  const { open, onClose } = props;
  const classes = { ...useCommonStyles(), ...useStyles() };
  const { values } = useFormikContext<DeliveryInstructionForm>();
  const { type } = useContext(DeliveryInstructionContext);
  const errors = validateDeliveryInstructionForm(values);
  const errorLabels = getDeliveryErrorLabels(errors);

  return (
    <ActionableDialog
      classes={{
        paper: classes.dialog,
      }}
      open={open}
      onClose={onClose}
    >
      <ActionableDialogTitle>{type === 'client' ? strings.title : ''}</ActionableDialogTitle>
      <ActionableDialogContent>
        <div className={classes.contentContainer}>
          <div className={clsx(classes.textError, classes.fontWeight700)}>
            {strings.infoIsNotEnough}
          </div>
          <VerticalSpace height={20}></VerticalSpace>
          <div>
            {strings.confirmErrorsPrompt}
            <span className={clsx(classes.textError, classes.fontWeight700)}>
              {errorLabels.map((label) => `「${label}」`).join(formStrings.comma)}
            </span>
          </div>
          <VerticalSpace height={20}></VerticalSpace>
          <div>{strings.thereIsEmptyField(type === 'admin')}</div>
          <VerticalSpace height={80}></VerticalSpace>
        </div>
      </ActionableDialogContent>
      <ActionableDialogContentDivider />
      <div className={classes.buttonContainer}>
        <Button
          color="primary"
          size="small"
          type="button"
          style={{ width: '100%' }}
          text={formStrings.prev}
          onClick={() => {
            onClose();
          }}
        />
      </div>
    </ActionableDialog>
  );
};
